import * as React from 'react';
import styled from 'styled-components';
import {NavLink} from 'react-router-dom';
import {BREAKPOINTS, COLORS, ComponentTextStyle, focusStyles} from '../styles';
import {SystemIcons} from '../icons';
import {ComponentMStyling} from '../styles';
import {Z_INDEXES} from '../styles';
import { useNavigate } from 'react-router';
import {IconButton} from '../Button';

const Container = styled.nav`
  margin: 20px 0;
  display: flex;
  justify-content: center;

  ${BREAKPOINTS.MEDIUM} {
    width: 75%;
  }
`;

const Items = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
`;

const Item = styled.li`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  & > * {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    text-decoration: none;

    ${ComponentMStyling(ComponentTextStyle.Regular, COLORS.neutral_600)}

    &:focus,
    &:focus-within {
      ${focusStyles}
    }
  }
  & > a:hover {
    z-index: ${Z_INDEXES.hover};
    color: ${COLORS.primary_800};
    background-color: ${COLORS.primary_20};
  }
  & > a:active {
    z-index: ${Z_INDEXES.active};
    color: ${COLORS.primary_700};
    background-color: ${COLORS.primary_100};
  }
  & > a.active {
    z-index: ${Z_INDEXES.active};
    color: ${COLORS.neutral_800};
    background-color: ${COLORS.neutral_20};
    cursor: default;
    &:hover {
      color: ${COLORS.primary_800};
      background-color: ${COLORS.primary_20};
    }
    &:active {
      color: ${COLORS.primary_700};
      background-color: ${COLORS.primary_100};
    }
  }
`;

const Dots = styled(Item)`
  cursor: not-allowed;
  &:after{
    ${ComponentMStyling(ComponentTextStyle.Regular, COLORS.neutral_600)}
    content: '...';
  }
`;

const ItemContent = styled('div')`
  text-align:center;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
  
  &.active:after{
    content: '';
    position: absolute;
    bottom: 4px;
    width: 32px;
    border-radius: 4px;
    height: 4px;
    background-color: ${COLORS.primary_500}
  }
  &.active:hover:after{
    background-color: ${COLORS.primary_700}
  }
  &.active:active:after{
    background-color: ${COLORS.primary_800}
  }
`

type PaginatorProps = {
  pageCount: number;
  currentPage: number;
  baseUrl: string;
  onPageChange?: (page: number) => boolean | void;
};

const Step = ({
  up = true,
  target = '',
  disabled = false,
  page,
  onPageChange,
  id,
}: {
  up: boolean;
  target: string;
  disabled: boolean;
  page: number;
  onPageChange?: (page: number) => void | boolean;
  id?: string;
}) => {
  const navigateHook = useNavigate();

  const handleButtonClick = () => {
    onPageChange && !onPageChange(page) && navigateHook(target);
  }

  return (
    <IconButton id={id}
                action={event => handleButtonClick()}
                variant={'secondary'}
                shape={'circular'}
                disabled={disabled}>
      {up ? <SystemIcons.ChevronRight/> : <SystemIcons.ChevronLeft/>}
    </IconButton>
  )
};

const Paginator = ({ pageCount = 1, currentPage = 1, baseUrl = '', onPageChange }: PaginatorProps) => {
  const pages = [];
  let from = 2;
  let to = 4;
  if (pageCount === 0) return null;

  if (pageCount > 1) {
    if (currentPage >= 4 && currentPage <= pageCount - 2) {
      from = currentPage - 1;
      to = currentPage + 1;
    } else if (pageCount - 2 > 1 && currentPage > pageCount - 2) {
      from = pageCount - 3;
      to = pageCount - 1;
    }
    if (to > pageCount) {
      to = pageCount;
    }
    for (let i = from; i <= to; i += 1) {
      pages.push(i);
    }
  }

  return (
    <Container>
      <Items>
        <Step up={false}
              target={`${baseUrl}/${currentPage - 1}`}
              page={currentPage - 1}
              disabled={currentPage === 1}
              onPageChange={onPageChange}
              id="paginator-left"/>
        {pages.findIndex(item => item === 1) === -1 && (
          <Item>
            <NavLink
              to={`${baseUrl}/1`}
              onClick={(e) => {
                onPageChange && onPageChange(1) && e.preventDefault();
              }}>
                <ItemContent className={currentPage === 1 ? 'active' : ''}>
                 <span>1</span>
                </ItemContent>
            </NavLink>
          </Item>
        )}
        {currentPage >= 4 && pageCount > 5 && pageCount && pages.findIndex(item => item === 1) === -1 && (
          <Dots/>
        )}
        {pages.map(page => (
          <Item key={page}>
            <NavLink
              to={`${baseUrl}/${page}`}
              onClick={(e) => {
                onPageChange && onPageChange(page) && e.preventDefault();
              }}>
                <ItemContent className={currentPage === page ? 'active' : ''}>
                 <span>{page}</span>
                </ItemContent>
            </NavLink>
          </Item>
        ))}
        {pageCount > 5 && currentPage <= pageCount - 3 && (
          <Dots/>
        )}
        {pages.findIndex(item => item === pageCount) === -1 && pageCount !== 1 && (
          <Item>
            <NavLink
              to={`${baseUrl}/${pageCount}`}
              onClick={(e) => {
                onPageChange && onPageChange(pageCount) && e.preventDefault();
              }}>
                <ItemContent className={currentPage === pageCount ? 'active' : ''}>
                 <span>{pageCount}</span>
                </ItemContent>
            </NavLink>
          </Item>
        )}
        <Step up
              target={`${baseUrl}/${currentPage + 1}`}
              page={currentPage + 1}
              disabled={currentPage === pageCount}
              onPageChange={onPageChange}
              id="paginator-right"/>
      </Items>
    </Container>
  );
};

export default Paginator;
